/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useState } from 'react'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { CalendarBlankIcon } from '@phosphor-icons/react'
import moment from 'moment'
import CalendarPicker from 'app/components/marketing/CalendarPicker'
import { Button } from 'app/components/ui/button'

const DAY = `YYYY-MM-DD`

/** A calendar day, picked with the app's own calendar rather than the browser's native date input
 *  (one grammar everywhere). Value in and out is a plain `YYYY-MM-DD` string, so callers never
 *  touch time zones: the engine and the condition serializer both speak calendar days here. */
export function DateInput({
  value,
  onChange,
  placeholder = `Select a date`,
  invalid,
  allowPastDates = true,
  allowFutureDates = true,
  'aria-label': ariaLabel,
}: {
  value?: string
  onChange: (value: string) => void
  placeholder?: string
  invalid?: boolean
  allowPastDates?: boolean
  allowFutureDates?: boolean
  'aria-label'?: string
}) {
  const [open, setOpen] = useState(false)
  const floating = useFloating({
    placement: `bottom-start`,
    middleware: [offset(6), flip({ padding: 10 })],
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floating.context),
    useDismiss(floating.context),
  ])
  const day = value ? moment(value, DAY, true) : undefined
  const label = day?.isValid() ? day.format(`MMM D, YYYY`) : undefined

  return (
    <>
      {/* Dressed as an input rather than a button, since it stands where a date field would. */}
      <Button
        variant="field"
        ref={floating.refs.setReference}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        data-value={value ?? ``}
        {...getReferenceProps()}
        className="w-full justify-between"
      >
        <span className={label ? undefined : `text-muted-foreground`}>{label ?? placeholder}</span>
        <CalendarBlankIcon size={14} className="size-3.5 text-minimal" />
      </Button>
      {open && (
        <FloatingPortal>
          <div
            ref={floating.refs.setFloating}
            {...getFloatingProps()}
            style={floating.floatingStyles}
            className="z-50 w-72 rounded-lg border border-default bg-surface p-2 shadow-elevation-2"
          >
            <CalendarPicker
              date={value}
              limitToMonth={false}
              allowPastDates={allowPastDates}
              allowFutureDates={allowFutureDates}
              onDateChange={(date) => {
                onChange(moment(date).format(DAY))
                setOpen(false)
              }}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
